import { z } from 'zod';

import { FetchTodayFortuneTellingError } from '../errors/FetchTodayFortuneTellingError';
import { InvalidBirthdateError } from '../errors/InvalidBirthdateError';

import { AppEnv } from './appEnv';
import { httpStatusCode } from './httpStatusCode';
import { createFailureResult, createSuccessResult, Result } from './result';
import { bffUrl } from './url/apiUrl';
import { validation } from './validation';

import type { Birthdate } from './loggedInUser';
import type { HttpsUrl } from './url';

// eslint-disable-next-line no-magic-numbers
export const zodiacSignIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

type ZodiacSignId = (typeof zodiacSignIds)[number];

const zodiacSignNames = [
  'おひつじ座',
  'おうし座',
  'ふたご座',
  'かに座',
  'しし座',
  'おとめ座',
  'てんびん座',
  'さそり座',
  'いて座',
  'やぎ座',
  'みずがめ座',
  'うお座',
];

type ZodiacSignName = (typeof zodiacSignNames)[number];

type ZodiacSignImageUrl = HttpsUrl;

type ZodiacSign = {
  id: ZodiacSignId;
  name: ZodiacSignName;
  imageUrl: ZodiacSignImageUrl;
};

type FetchTodayFortuneTellingInvalidBirthdateErrorResponse = {
  type: 'INVALID_BIRTHDATE';
  title: 'Birthdate is invalid.';
  // eslint-disable-next-line no-magic-numbers
  status: 400;
  detail: string;
};

const fetchTodayFortuneTellingInvalidBirthdateErrorResponseSchema = z.object({
  type: z.literal('INVALID_BIRTHDATE'),
  title: z.literal('Birthdate is invalid.'),
  // eslint-disable-next-line no-magic-numbers
  status: z.literal(400),
  // eslint-disable-next-line no-magic-numbers
  detail: z.string().min(1),
});

const isFetchTodayFortuneTellingInvalidBirthdateErrorResponse = (
  value: unknown,
): value is FetchTodayFortuneTellingInvalidBirthdateErrorResponse =>
  validation(fetchTodayFortuneTellingInvalidBirthdateErrorResponseSchema, value)
    .isValidate;

type TodayFortuneTellingRank = ZodiacSignId;

type FetchTodayFortuneTellingSuccessResponse = {
  zodiacSign: ZodiacSign;
  adviceMessage: string;
  rank: TodayFortuneTellingRank;
};

type FetchTodayFortuneTellingResponse =
  FetchTodayFortuneTellingSuccessResponse & {
    xRequestId: string;
  };

type FetchTodayFortuneTelling = (
  appEnv: AppEnv,
  birthdate: Birthdate,
  uniqueIdGenerator: () => string,
) => Promise<Result<FetchTodayFortuneTellingResponse, InvalidBirthdateError>>;

// eslint-disable-next-line max-statements
export const fetchTodayFortuneTelling: FetchTodayFortuneTelling = async (
  appEnv,
  birthdate,
  uniqueIdGenerator,
) => {
  const xRequestId = uniqueIdGenerator();

  const options = {
    method: 'GET',
    headers: {
      'X-Request-Id': xRequestId,
    },
  };

  const response = await fetch(
    `${bffUrl(appEnv)}/fortune-telling/${birthdate}`,
    options,
  );

  if (response.status !== httpStatusCode.ok) {
    const responseBody = await response.json();
    if (isFetchTodayFortuneTellingInvalidBirthdateErrorResponse(responseBody)) {
      return createFailureResult(new InvalidBirthdateError(xRequestId));
    }

    throw new FetchTodayFortuneTellingError(xRequestId);
  }

  const responseBody =
    (await response.json()) as FetchTodayFortuneTellingSuccessResponse;

  const fetchTodayFortuneTellingResponse = { ...responseBody, xRequestId };

  return createSuccessResult(fetchTodayFortuneTellingResponse);
};
